import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Card,
  CardContent,
  CircularProgress,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import apiService from '../services/api.service';
import ResponsiveDialog from '../components/common/ResponsiveDialog';
import ResponsiveTable from '../components/common/ResponsiveTable';
import {
  McsrDrillTransaction,
  McsrProjectOption,
  McsrReportPayload,
  McsrReportRow,
  McsrTrendPoint,
  fmtMcsr,
  fmtMcsrCompact,
  fmtMcsrPercent,
} from '../types/mcsr';

/**
 * MCSR Dashboard view (spec §6.2.2):
 *  - KPI cards: Current Budget (4), Expended Upto Date (6), Committed to
 *    Date (8), Current Final Forecast (10), Over/UnderRun (12), Overall Profit %
 *  - Charts: Budget vs Expended vs Forecast by Cost Group (bar), Expended
 *    This Period trend by month (line), cost distribution by Classification
 *    (donut), Income vs Cost comparison (Financial Analysis)
 *  - Drill-down: cost-group click → underlying transactions dialog.
 */

const PALETTE = ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b', '#e377c2', '#17becf'];

function monthBounds(month: string): { fdate: string; tdate: string } {
  const [y, m] = month.split('-').map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { fdate: `${month}-01`, tdate: `${month}-${String(last).padStart(2, '0')}` };
}

interface KpiCardProps {
  label: string;
  value: string;
  color?: string;
  sub?: string;
}

function KpiCard({ label, value, color, sub }: KpiCardProps) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent sx={{ py: 1.5 }}>
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{label}</Typography>
        <Typography sx={{ fontSize: 22, fontWeight: 700, color: color || 'text.primary' }}>{value}</Typography>
        {sub && <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{sub}</Typography>}
      </CardContent>
    </Card>
  );
}

export default function McsrDashboard() {
  const [projects, setProjects] = useState<McsrProjectOption[]>([]);
  const [project, setProject] = useState<McsrProjectOption | null>(null);
  const [months, setMonths] = useState<string[]>([]);
  const [month, setMonth] = useState<string>(new Date().toISOString().slice(0, 7));
  const [rangeMonths, setRangeMonths] = useState<number>(6);
  const [payload, setPayload] = useState<McsrReportPayload | null>(null);
  const [trend, setTrend] = useState<McsrTrendPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [drill, setDrill] = useState<{ open: boolean; title: string; rows: McsrDrillTransaction[]; loading: boolean }>({
    open: false, title: '', rows: [], loading: false,
  });

  const { fdate, tdate } = useMemo(() => monthBounds(month), [month]);
  const trendFrom = useMemo(() => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(Date.UTC(y, m - rangeMonths, 1));
    return d.toISOString().slice(0, 10);
  }, [month, rangeMonths]);

  useEffect(() => {
    apiService.getMcsrProjects()
      .then((res: any) => {
        const list = res.data || [];
        setProjects(list);
        if (list.length === 1) setProject(list[0]);
      })
      .catch(() => setToast('Failed to load MCSR projects'));
  }, []);

  useEffect(() => {
    apiService.getMcsrMonths(project?.project)
      .then((res: any) => {
        const list: string[] = Array.from(new Set((res.data || []).map((d: string) => String(d).slice(0, 7)))) as string[];
        setMonths(list);
        if (list.length && !list.includes(month)) setMonth(list[0]);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.project]);

  const load = useCallback(async () => {
    if (!project) { setPayload(null); setTrend([]); return; }
    setLoading(true);
    try {
      const [rep, tr] = await Promise.all([
        apiService.getMcsrReport(project.project, fdate, tdate).catch((e: any) => {
          if (e?.response?.status === 404) return { data: null };
          throw e;
        }),
        apiService.getMcsrTrend(project.project, trendFrom, tdate),
      ]);
      setPayload((rep as any).data || null);
      setTrend((tr as any).data || []);
    } catch (e: any) {
      setToast(e?.response?.data?.message || 'Failed to load MCSR dashboard');
    } finally {
      setLoading(false);
    }
  }, [project, fdate, tdate, trendFrom]);

  useEffect(() => { void load(); }, [load]);

  const summaryLeaves: McsrReportRow[] = useMemo(
    () => (payload?.summary || []).filter((r) => r.kind === 'detail'),
    [payload]
  );
  const grand = useMemo(
    () => (payload?.summary || []).find((r) => r.kind === 'grand-total'),
    [payload]
  );

  const byGroupData = summaryLeaves.map((r) => ({
    group: r.costGroup || '',
    'Current Budget': r.currentBudget,
    'Expended Upto Date': r.expendedUptoDate,
    'Current Final Forecast': r.currentFinalForecast,
  }));

  const byClassData = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of summaryLeaves) {
      const key = r.classificationName || '';
      map.set(key, (map.get(key) || 0) + Math.max(0, r.expendedUptoDate));
    }
    return Array.from(map.entries()).map(([name, value]) => ({ name, value }));
  }, [summaryLeaves]);

  const trendData = trend.map((t) => ({
    month: t.month,
    'Expended This Period': t.expendedThisPeriod,
    'Committed to Date': t.committedToDate,
  }));

  const incomeVsCost = payload
    ? [
        { name: 'This Period', Income: payload.financialAnalysis.totalIncome.periodActual, Cost: payload.financialAnalysis.totalCost.periodActual },
        { name: 'To Date', Income: payload.financialAnalysis.totalIncome.toDateActual, Cost: payload.financialAnalysis.totalCost.toDateActual },
      ]
    : [];

  const openDrill = async (costGroup: string) => {
    if (!project) return;
    setDrill({ open: true, title: `Transactions — ${costGroup} (${project.project})`, rows: [], loading: true });
    try {
      const res: any = await apiService.getMcsrTransactions(project.project, fdate, tdate);
      setDrill((d) => ({ ...d, rows: res.data || [], loading: false }));
    } catch {
      setDrill((d) => ({ ...d, loading: false }));
      setToast('Transaction drill-down failed (are the document sync jobs scheduled?)');
    }
  };

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>MCSR Dashboard</Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Autocomplete
            sx={{ minWidth: 320, flex: 1 }}
            options={projects}
            value={project}
            onChange={(_, v) => setProject(v)}
            getOptionLabel={(o) => `${o.project} — ${o.projectName}`}
            isOptionEqualToValue={(a, b) => a.project === b.project}
            renderInput={(params) => <TextField {...params} label="Project" />}
          />
          <FormControl sx={{ minWidth: 170 }}>
            <InputLabel id="mcsr-dash-month">Period (month)</InputLabel>
            <Select
              labelId="mcsr-dash-month"
              label="Period (month)"
              value={months.includes(month) ? month : ''}
              onChange={(e) => setMonth(String(e.target.value))}
            >
              {months.map((m) => (<MenuItem key={m} value={m}>{m}</MenuItem>))}
              {!months.length && <MenuItem value={month}>{month}</MenuItem>}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 140 }}>
            <InputLabel id="mcsr-dash-range">Trend range</InputLabel>
            <Select
              labelId="mcsr-dash-range"
              label="Trend range"
              value={rangeMonths}
              onChange={(e) => setRangeMonths(Number(e.target.value))}
            >
              <MenuItem value={3}>3 months</MenuItem>
              <MenuItem value={6}>6 months</MenuItem>
              <MenuItem value={12}>12 months</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {!project && <Alert severity="info">Select a project to see its MCSR dashboard.</Alert>}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress aria-label="Loading dashboard" />
        </Box>
      )}

      {!loading && project && !payload && (
        <Alert severity="info">No MCSR snapshot for this project/month.</Alert>
      )}

      {!loading && payload && grand && (
        <>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={4} md={2}>
              <KpiCard label="Current Budget (4)" value={fmtMcsrCompact(grand.currentBudget)} sub={fmtMcsr(grand.currentBudget)} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <KpiCard label="Expended Upto Date (6)" value={fmtMcsrCompact(grand.expendedUptoDate)} sub={fmtMcsr(grand.expendedUptoDate)} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <KpiCard label="Committed to Date (8)" value={fmtMcsrCompact(grand.committedToDate)} sub={fmtMcsr(grand.committedToDate)} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <KpiCard label="Current Final Forecast (10)" value={fmtMcsrCompact(grand.currentFinalForecast)} sub={fmtMcsr(grand.currentFinalForecast)} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <KpiCard
                label="Over/UnderRun (12)"
                value={fmtMcsrCompact(grand.overUnderRun)}
                color={grand.overUnderRun > 0 ? '#d62728' : grand.overUnderRun < 0 ? '#2ca02c' : undefined}
                sub={fmtMcsr(grand.overUnderRun)}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <KpiCard
                label="Overall Profit % (To Date)"
                value={fmtMcsrPercent(payload.financialAnalysis.overallProfit.toDateActual)}
                color={payload.financialAnalysis.overallProfit.toDateActual >= 0 ? '#2ca02c' : '#d62728'}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} lg={7}>
              <Paper variant="outlined" sx={{ p: 2, height: 380 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>
                  Budget vs Expended vs Forecast by Cost Group (click a bar group to drill)
                </Typography>
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={byGroupData} onClick={(s: any) => s?.activeLabel && void openDrill(String(s.activeLabel))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="group" tick={{ fontSize: 10 }} interval={0} angle={-18} textAnchor="end" height={58} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={(v: number) => fmtMcsrCompact(v)} />
                    <ChartTooltip formatter={(v: any) => fmtMcsr(Number(v))} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="Current Budget" fill={PALETTE[0]} />
                    <Bar dataKey="Expended Upto Date" fill={PALETTE[1]} />
                    <Bar dataKey="Current Final Forecast" fill={PALETTE[2]} />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} lg={5}>
              <Paper variant="outlined" sx={{ p: 2, height: 380 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>
                  Cost Distribution by Classification (Expended Upto Date)
                </Typography>
                <ResponsiveContainer width="100%" height={320}>
                  <PieChart>
                    <Pie
                      data={byClassData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius="52%"
                      outerRadius="80%"
                      label={(e: any) => `${e.name}: ${fmtMcsrCompact(e.value)}`}
                    >
                      {byClassData.map((_, i) => (<Cell key={i} fill={PALETTE[i % PALETTE.length]} />))}
                    </Pie>
                    <ChartTooltip formatter={(v: any) => fmtMcsr(Number(v))} />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} lg={7}>
              <Paper variant="outlined" sx={{ p: 2, height: 340 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>
                  Expended This Period — Monthly Trend
                </Typography>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={(v: number) => fmtMcsrCompact(v)} />
                    <ChartTooltip formatter={(v: any) => fmtMcsr(Number(v))} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="Expended This Period" stroke={PALETTE[3]} dot />
                    <Line type="monotone" dataKey="Committed to Date" stroke={PALETTE[4]} dot />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} lg={5}>
              <Paper variant="outlined" sx={{ p: 2, height: 340 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>
                  Income vs Cost (Financial Analysis)
                </Typography>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={incomeVsCost}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={(v: number) => fmtMcsrCompact(v)} />
                    <ChartTooltip formatter={(v: any) => fmtMcsr(Number(v))} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="Income" fill={PALETTE[2]} />
                    <Bar dataKey="Cost" fill={PALETTE[3]} />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}

      <ResponsiveDialog open={drill.open} onClose={() => setDrill((d) => ({ ...d, open: false }))} maxWidth="md" fullWidth>
        <DialogTitle>{drill.title}</DialogTitle>
        <DialogContent>
          {drill.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress aria-label="Loading transactions" />
            </Box>
          ) : (
            <ResponsiveTable
              ariaLabel="MCSR drill-down transactions"
              rows={drill.rows}
              getRowKey={(r: McsrDrillTransaction, i: number) => `${r.source}|${r.docEntry}|${i}`}
              emptyMessage="No documents found for this period (check that the MCSR document sync jobs are scheduled)."
              columns={[
                { id: 'source', header: 'Source', cell: (r: McsrDrillTransaction) => r.source },
                { id: 'docnum', header: 'Doc No.', cell: (r: McsrDrillTransaction) => r.docNum || r.docEntry },
                { id: 'date', header: 'Date', cell: (r: McsrDrillTransaction) => r.docDate || '' },
                { id: 'amount', header: 'Amount', align: 'right', cell: (r: McsrDrillTransaction) => fmtMcsr(r.amount) },
                { id: 'memo', header: 'Memo', cell: (r: McsrDrillTransaction) => r.memo || '' },
              ]}
            />
          )}
        </DialogContent>
      </ResponsiveDialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
}
